import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

/**
 * Text-input field with built-in label, inline error, and the matching
 * accessibility wiring (`aria-invalid`, `aria-describedby`, label/input
 * association).
 *
 * Visually styled per Figma: white pill background, dark inner text, large
 * rounded corners. Error state swaps text color + adds a red ring without
 * shifting layout.
 *
 * The visible label is mandatory for accessibility. Hide it from sighted
 * users when the design calls for a placeholder-only look by passing
 * `visuallyHiddenLabel`.
 */
type FormFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> & {
  label: string;
  visuallyHiddenLabel?: boolean;
  error?: string | null;
  helperText?: ReactNode;
  /** Extra class added to the <input> only (label/wrapper untouched). */
  inputClassName?: string;
};

export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(function FormField(
  { label, visuallyHiddenLabel, error, helperText, inputClassName, id, ...inputProps },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? `field-${reactId}`;
  const errorId = `${inputId}-error`;
  const helperId = helperText ? `${inputId}-helper` : undefined;
  const describedBy =
    [error ? errorId : null, helperId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="mx-auto flex w-full max-w-[500px] flex-col gap-1">
      <label
        htmlFor={inputId}
        className={
          visuallyHiddenLabel
            ? 'sr-only'
            : "mb-1 font-['Arial',sans-serif] text-[14px] uppercase tracking-[1px] text-white/80"
        }
      >
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={[
          // text-center matches the Figma input style — placeholder and
          // typed value both render center-aligned across every reg step.
          "h-[42px] w-full rounded-[20px] bg-white px-5 text-center font-['Arial',sans-serif] text-[18px] text-[#1f1a26] placeholder:text-[#7a7a7a]",
          'shadow-[inset_0_2px_2px_rgba(0,0,0,0.08)]',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00ffb2] focus-visible:ring-offset-2 focus-visible:ring-offset-[#3a2f44]',
          error ? 'ring-2 ring-[#ff4479]' : '',
          inputClassName ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
        {...inputProps}
      />
      {helperText && (
        <p id={helperId} className="font-['Arial',sans-serif] text-[12px] text-white/70">
          {helperText}
        </p>
      )}
      {error && (
        <p
          id={errorId}
          role="alert"
          className="font-['Arial',sans-serif] text-[13px] leading-tight text-[#ffb3c5]"
        >
          {error}
        </p>
      )}
    </div>
  );
});
